import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Save, RefreshCw, Power, PowerOff, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * RouletteConfigPanel — admin controls for the in-house American Roulette table.
 * Wraps GET/PUT /api/admin/roulette/config. Mirrors the Craps/BJ admin panels.
 */
export const RouletteConfigPanel = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [edited, setEdited] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [chipsRaw, setChipsRaw] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/roulette/config`, { withCredentials: true });
            const { defaults: d, ...rest } = data;
            setCfg(rest); setEdited(rest); setDefaults(d);
            setChipsRaw((rest.chip_denominations || []).join(', '));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Roulette config');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const dirty = JSON.stringify(cfg) !== JSON.stringify(edited);

    const parseChips = (raw) => raw.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);

    const save = async () => {
        if (!edited) return;
        const chips = parseChips(chipsRaw);
        if (chips.length === 0) { toast.error('Enter at least one chip denomination'); return; }
        const payload = { ...edited, chip_denominations: chips };
        setSaving(true);
        try {
            await axios.put(`${API}/admin/roulette/config`, payload, { withCredentials: true });
            toast.success('Roulette config saved');
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map((x) => x?.msg || JSON.stringify(x)).join('; ') :
                'Save failed';
            toast.error(msg);
        } finally { setSaving(false); }
    };

    const applyDefaults = () => {
        if (!defaults) return;
        setEdited({ ...defaults });
        setChipsRaw((defaults.chip_denominations || []).join(', '));
    };

    if (!edited) return <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="roulette-config-loading">Loading Roulette config…</div>;

    const set = (k, v) => setEdited((e) => ({ ...e, [k]: v }));

    return (
        <div className="card-dark p-5 space-y-5" data-testid="roulette-config-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-[#c11b1b]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Roulette Config</h3>
                    <span className="text-[10px] text-[#A0AAB2]">American · 0 & 00</span>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} disabled={loading} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1" data-testid="roulette-config-reload">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Reload
                    </button>
                    <button onClick={applyDefaults} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1" data-testid="roulette-config-defaults">
                        <RotateCcw className="w-3.5 h-3.5" />Defaults
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-3">
                <div className="flex items-center gap-2">
                    {edited.enabled ? <Power className="w-4 h-4 text-[#39FF14]" /> : <PowerOff className="w-4 h-4 text-[#FF3B30]" />}
                    <div>
                        <p className="text-sm font-bold text-white">Table {edited.enabled ? 'open' : 'closed'}</p>
                        <p className="text-[11px] text-[#A0AAB2]">When closed, players see a &ldquo;table on break&rdquo; screen.</p>
                    </div>
                </div>
                <Switch checked={edited.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="roulette-config-enabled" />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-3">
                <div>
                    <p className="text-sm font-bold text-white">Maintenance mode</p>
                    <p className="text-[11px] text-[#A0AAB2]">Blocks new spins even when the table is enabled.</p>
                </div>
                <Switch checked={edited.maintenance_mode} onCheckedChange={(v) => set('maintenance_mode', v)} data-testid="roulette-config-maintenance" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Min bet ($)</Label>
                    <Input type="number" step="0.25" min="0.25" value={edited.min_bet} onChange={(e) => set('min_bet', Number(e.target.value))} className="mt-1" data-testid="roulette-config-min-bet" /></div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Inside max ($)</Label>
                    <Input type="number" step="1" min="1" value={edited.max_inside_bet} onChange={(e) => set('max_inside_bet', Number(e.target.value))} className="mt-1" data-testid="roulette-config-max-inside" /></div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Outside max ($)</Label>
                    <Input type="number" step="1" min="1" value={edited.max_outside_bet} onChange={(e) => set('max_outside_bet', Number(e.target.value))} className="mt-1" data-testid="roulette-config-max-outside" /></div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Table max / spin ($)</Label>
                    <Input type="number" step="1" min="1" value={edited.max_total_wager} onChange={(e) => set('max_total_wager', Number(e.target.value))} className="mt-1" data-testid="roulette-config-max-total" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Max payout / round ($)</Label>
                    <Input type="number" step="100" min="1" value={edited.max_payout} onChange={(e) => set('max_payout', Number(e.target.value))} className="mt-1" data-testid="roulette-config-max-payout" /></div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">History length</Label>
                    <Input type="number" step="1" min="1" max="200" value={edited.history_length} onChange={(e) => set('history_length', Number(e.target.value))} className="mt-1" data-testid="roulette-config-history" /></div>
            </div>

            <div>
                <Label className="text-[10px] uppercase text-[#A0AAB2]">Chip denominations (comma-separated)</Label>
                <Input value={chipsRaw} onChange={(e) => setChipsRaw(e.target.value)} placeholder="1, 5, 10, 25, 50, 100, 500" className="mt-1" data-testid="roulette-config-chips" />
                <p className="text-[10px] text-[#A0AAB2] mt-1">Preview: {parseChips(chipsRaw).map((v) => `$${v}`).join(' · ') || '—'}</p>
            </div>

            <div>
                <Label className="text-[10px] uppercase text-[#A0AAB2]">Help text (shown in player rules panel)</Label>
                <Input value={edited.help_text || ''} onChange={(e) => set('help_text', e.target.value)} className="mt-1" data-testid="roulette-config-help" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Animation speed</Label>
                    <select value={edited.animation_speed} onChange={(e) => set('animation_speed', e.target.value)} className="w-full mt-1 rounded-md border border-white/10 bg-[#0e0e0e] text-white text-sm px-2 py-1.5" data-testid="roulette-config-speed">
                        <option value="normal">Normal</option>
                        <option value="fast">Fast</option>
                    </select>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Sounds</Label>
                    <Switch checked={edited.sounds_enabled} onCheckedChange={(v) => set('sounds_enabled', v)} data-testid="roulette-config-sounds" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Hot / Cold stats</Label>
                    <Switch checked={edited.hot_cold_enabled} onCheckedChange={(v) => set('hot_cold_enabled', v)} data-testid="roulette-config-hotcold" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Favorites</Label>
                    <Switch checked={edited.favorites_enabled} onCheckedChange={(v) => set('favorites_enabled', v)} data-testid="roulette-config-favs" />
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={!dirty || saving} className="bg-[#c11b1b] hover:bg-[#a01515] text-white font-black" data-testid="roulette-config-save">
                    <Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </Button>
            </div>
        </div>
    );
};

export default RouletteConfigPanel;
